<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { RouterLink, RouterView, useRouter } from "vue-router";
import ToastTray from "@/components/ToastTray.vue";
import BrandSwitcher from "@/components/BrandSwitcher.vue";
import ThemeToggle from "@/components/ThemeToggle.vue";
import LocaleSwitcher from "@/components/LocaleSwitcher.vue";
import NotificationBell from "@/components/NotificationBell.vue";
import { useAuthStore } from "@/stores/auth";
import { useNotificationsStore } from "@/stores/notifications";
import { useSessionRefresh } from "@/composables/useSessionRefresh";
// Touch the theme + i18n stores so their constructors wire up the
// prefers-color-scheme media query / persisted locale on first paint.
import { useThemeStore } from "@/stores/theme";
import { useI18nStore } from "@/stores/i18n";
import web1Logo from "@/assets/web1-logo.svg?url";

const auth = useAuthStore();
const router = useRouter();
const notifications = useNotificationsStore();
useThemeStore();
useI18nStore();
// Aktiviteetti-pohjainen silent-refresh access-tokenille — pidetään
// käyttäjä kirjautuneena niin kauan kun hän on aktiivinen.
useSessionRefresh();
const { t } = useI18n();

// Rehydrate `auth.user` on app boot when a persisted token exists.
// Without this, a full page reload (the BrandSwitcher does
// window.location.reload() to refresh every brand-scoped store)
// leaves `auth.user` null until something — usually DashboardView's
// onMounted — calls fetchMe explicitly, and the sidebar's
// permission-gated nav items collapse in the meantime. Fire-and-
// forget: the call is idempotent, fast, and consumers tolerate the
// brief null-user gap.
if (auth.isAuthenticated && !auth.user) {
  void auth.fetchMe();
}

watch(
  () => auth.isAuthenticated,
  (yes) => {
    if (yes) notifications.startPolling();
    else notifications.stopPolling();
  },
  { immediate: true },
);

// Permission-gated visibility. Each link uses the corresponding read
// permission; absent users (login screen) get an empty sidebar.
function has(perm: string): boolean {
  return auth.user?.permissions?.includes(perm) ?? false;
}
const canSeeAudit = computed(() => has("audit_log:read"));
const canSeeEmailTemplates = computed(() => has("email_template:read"));
const canSeeBrands = computed(() => has("brand:read"));
const canSeeHmacClients = computed(
  () => has("panel_api_key:read") || has("mcp_client:read"),
);
const canSeeImports = computed(() => has("import:read"));
const canSeeProducts = computed(() => has("product:read"));
const canSeeProductFamilies = computed(() => has("product_family:read"));
const canSeeProductTemplates = computed(() => has("product-template:read"));
const canSeeProductCategories = computed(() => has("product-category:read"));
const canSeeUsers = computed(() => has("user:read"));
const canSeeRoles = computed(() => has("role:read"));
const canSeeSettings = computed(() => has("system_settings:read"));
const canSeeAdmin = computed(
  () => canSeeUsers.value || canSeeRoles.value || canSeeSettings.value,
);
const canChangePassword = computed(
  () => (auth.user?.authProvider ?? "LOCAL") === "LOCAL",
);

// User dropdown — click toggles, outside-click closes.
const menuOpen = ref(false);
function toggleMenu(): void {
  menuOpen.value = !menuOpen.value;
}
function closeMenu(): void {
  menuOpen.value = false;
}
function handleOutsideClick(e: MouseEvent): void {
  const target = e.target as HTMLElement | null;
  if (!target) return;
  if (!target.closest(".user-dropdown")) {
    menuOpen.value = false;
  }
}
document.addEventListener("click", handleOutsideClick);
onBeforeUnmount(() => document.removeEventListener("click", handleOutsideClick));

function goToChangePassword(): void {
  closeMenu();
  router.push("/change-password");
}

function doLogout(): void {
  closeMenu();
  auth.logout();
  router.push("/login");
}
</script>

<template>
  <div class="layout" :class="{ 'no-sidebar': !auth.isAuthenticated }">
    <aside v-if="auth.isAuthenticated" class="sidebar" aria-label="Päänavigointi">
      <RouterLink to="/dashboard" class="brand-block">
        <img :src="web1Logo" alt="Web1" class="brand-logo" />
        <div class="brand-sub">{{ t("app.brand") }} · Admin</div>
      </RouterLink>
      <nav class="nav">
        <RouterLink to="/dashboard" class="nav-item">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <rect x="3" y="3" width="7" height="7" rx="1.2" /><rect x="14" y="3" width="7" height="7" rx="1.2" />
            <rect x="3" y="14" width="7" height="7" rx="1.2" /><rect x="14" y="14" width="7" height="7" rx="1.2" />
          </svg>
          <span>{{ t("app.nav.dashboard") }}</span>
        </RouterLink>
        <RouterLink to="/customers" class="nav-item">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <circle cx="12" cy="8" r="3.5" /><path d="M4.5 20c.7-3.6 3.7-5.5 7.5-5.5s6.8 1.9 7.5 5.5" />
          </svg>
          <span>{{ t("app.nav.customers") }}</span>
        </RouterLink>
        <RouterLink to="/contracts" class="nav-item">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M6 3h9l4 4v14H6z" /><path d="M14 3v5h5" /><path d="M9 12h7M9 16h7" />
          </svg>
          <span>{{ t("app.nav.contracts") }}</span>
        </RouterLink>
        <RouterLink v-if="canSeeProducts" to="/products" class="nav-item">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M4 7l8-4 8 4-8 4-8-4z" /><path d="M4 7v10l8 4 8-4V7" /><path d="M12 11v10" />
          </svg>
          <span>{{ t("app.nav.products") }}</span>
        </RouterLink>
        <RouterLink v-if="canSeeProductFamilies" to="/product-families" class="nav-item">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <rect x="3" y="3" width="7" height="7" rx="1.2" /><rect x="14" y="3" width="7" height="7" rx="1.2" />
            <rect x="3" y="14" width="7" height="7" rx="1.2" /><rect x="14" y="14" width="7" height="7" rx="1.2" />
          </svg>
          <span>{{ t("app.nav.productFamilies") }}</span>
        </RouterLink>
        <RouterLink v-if="canSeeProductTemplates" to="/product-templates" class="nav-item">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M4 6h16M4 12h16M4 18h10" /><circle cx="20" cy="18" r="2" />
          </svg>
          <span>Tuote-templates</span>
        </RouterLink>
        <RouterLink v-if="canSeeProductCategories" to="/product-categories" class="nav-item">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M3 7h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 17h7v4H3z" />
          </svg>
          <span>Kategoriat</span>
        </RouterLink>
        <RouterLink to="/reports" class="nav-item">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
          </svg>
          <span>{{ t("app.nav.reports") }}</span>
        </RouterLink>
        <RouterLink v-if="canSeeBrands" to="/brands" class="nav-item">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M5 3h14v6a7 7 0 1 1-14 0z" /><path d="M9 21h6M12 16v5" />
          </svg>
          <span>{{ t("app.nav.brands") }}</span>
        </RouterLink>
        <RouterLink v-if="canSeeEmailTemplates" to="/email-templates" class="nav-item">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 7l9 6 9-6" />
          </svg>
          <span>{{ t("app.nav.emailTemplates") }}</span>
        </RouterLink>
        <RouterLink v-if="canSeeHmacClients" to="/hmac-clients" class="nav-item">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <circle cx="9" cy="14" r="4" /><path d="M12 12l9-9M16 4h4v4" />
          </svg>
          <span>{{ t("app.nav.hmacClients") }}</span>
        </RouterLink>
        <RouterLink v-if="canSeeImports" to="/imports/hostbill" class="nav-item">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M12 3v12M7 10l5 5 5-5" /><path d="M4 17v3h16v-3" />
          </svg>
          <span>{{ t("app.nav.imports") }}</span>
        </RouterLink>
        <RouterLink v-if="canSeeAudit" to="/audit-logs" class="nav-item">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" />
          </svg>
          <span>{{ t("app.nav.audit") }}</span>
        </RouterLink>

        <div v-if="canSeeAdmin" class="nav-section">
          <span class="nav-section-label">{{ t("app.nav.adminSection") }}</span>
        </div>
        <RouterLink v-if="canSeeUsers" to="/users" class="nav-item">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <circle cx="12" cy="8" r="3.5" /><path d="M4.5 20c.7-3.6 3.7-5.5 7.5-5.5s6.8 1.9 7.5 5.5" />
          </svg>
          <span>{{ t("app.nav.users") }}</span>
        </RouterLink>
        <RouterLink v-if="canSeeRoles" to="/roles" class="nav-item">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M12 3l9 4-9 4-9-4 9-4z" /><path d="M5 11v5l7 4 7-4v-5" />
          </svg>
          <span>{{ t("app.nav.roles") }}</span>
        </RouterLink>
        <RouterLink v-if="canSeeSettings" to="/settings" class="nav-item">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.7 1.7 0 0 0 .4 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.4 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.9.4l-.1.1A2 2 0 1 1 4.3 17l.1-.1a1.7 1.7 0 0 0 .4-1.9 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.4-1.9l-.1-.1A2 2 0 1 1 7 4.3l.1.1a1.7 1.7 0 0 0 1.9.4h.1a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.9-.4l.1-.1A2 2 0 1 1 19.7 7l-.1.1a1.7 1.7 0 0 0-.4 1.9v.1a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
          </svg>
          <span>{{ t("app.nav.settings") }}</span>
        </RouterLink>
      </nav>
    </aside>

    <div class="content">
      <header v-if="auth.isAuthenticated" class="topbar">
        <div class="topbar-left">
          <BrandSwitcher v-if="canSeeBrands" />
        </div>
        <div class="topbar-right">
          <LocaleSwitcher />
          <ThemeToggle />
          <NotificationBell />
          <div class="user-dropdown">
            <button
              type="button"
              class="user-trigger"
              :aria-expanded="menuOpen ? 'true' : 'false'"
              aria-haspopup="menu"
              @click="toggleMenu"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" class="user-icon">
                <circle cx="12" cy="8" r="3.5" /><path d="M4.5 20c.7-3.6 3.7-5.5 7.5-5.5s6.8 1.9 7.5 5.5" />
              </svg>
              <span class="user-email">{{ auth.user?.displayName ?? auth.user?.email }}</span>
              <span class="caret" aria-hidden="true">▾</span>
            </button>
            <div v-if="menuOpen" class="dropdown-menu" role="menu">
              <button
                v-if="canChangePassword"
                type="button"
                role="menuitem"
                class="menu-item"
                @click="goToChangePassword"
              >{{ t("app.userMenu.changePassword") }}</button>
              <button
                type="button"
                role="menuitem"
                class="menu-item"
                @click="doLogout"
              >{{ t("app.userMenu.logout") }}</button>
            </div>
          </div>
          <button
            type="button"
            class="logout-direct"
            :title="t('app.userMenu.logout')"
            @click="doLogout"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3" /><path d="M10 17l-5-5 5-5M5 12h12" />
            </svg>
            <span>{{ t("app.userMenu.logout") }}</span>
          </button>
        </div>
      </header>
      <main>
        <RouterView />
      </main>
    </div>
    <ToastTray />
  </div>
</template>

<style>
/*
 * Theme tokens. Light values are the defaults; the dark overrides
 * are scoped to `html[data-theme="dark"]` (the theme store applies it
 * on <html>). The sidebar uses its own fixed dark palette regardless
 * of theme — that mirrors the Web1 Billing layout the design is based
 * on, where the sidebar acts as the brand surface.
 */
:root {
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 14px;
  color-scheme: light;

  --bg: #f5f6f8;
  --surface: #ffffff;
  --surface-2: #f9fafb;
  --surface-3: #f3f4f6;
  --text: #1f2937;
  --text-strong: #111827;
  --text-muted: #6b7280;
  --border: #e5e7eb;
  --border-strong: #d1d5db;
  /* Web1 brand magenta — pulled straight from the logo SVG. */
  --accent: #ff0083;
  --accent-strong: #cc006a;
  --accent-bg: #fde7f0;
  --error: #b91c1c;
  --error-bg: #fef2f2;
  --error-border: #fecaca;
  --success: #166534;
  --success-bg: #dcfce7;
  --warn: #92400e;
  --warn-bg: #fef3c7;

  /* Sidebar — vivid raspberry that matches the Web1 support-
     system layout (web1.fi/asiakaspalvelu). Fixed across light
     and dark so the sidebar always reads as the brand surface,
     with the magenta logo accent (#ff0083) used for the active
     nav item. */
  --sidebar-bg: #9c1f4f;
  --sidebar-bg-2: #b02560;
  --sidebar-text: #f9e3ec;
  --sidebar-text-muted: #e3a4bd;
  --sidebar-active-bg: #ff0083;
  --sidebar-active-text: #ffffff;
  --sidebar-border: #862043;
}

:root[data-theme="dark"] {
  color-scheme: dark;

  /* Warm dark palette tuned around the magenta brand: bg has a
     slight plum tint so the magenta accent doesn't feel disjointed,
     surfaces stay distinct, and text uses a warm off-white. */
  --bg: #1a141a;
  --surface: #241c25;
  --surface-2: #2c2330;
  --surface-3: #3a2e3e;
  --text: #e2d4dc;
  --text-strong: #f5ebef;
  --text-muted: #a89aa1;
  --border: #3d2d39;
  --border-strong: #523c4d;
  --accent: #ff5fa8;
  --accent-strong: #ff8cc1;
  --accent-bg: #3a1a2c;
  --error: #fca5a5;
  --error-bg: #3a1d1d;
  --error-border: #6e2424;
  --success: #86efac;
  --success-bg: #1a3325;
  --warn: #fcd34d;
  --warn-bg: #3a2e0e;
}

* { box-sizing: border-box; }
body { margin: 0; background: var(--bg); color: var(--text); }

/* ─── Layout shell ─────────────────────────────────────────────── */
.layout {
  min-height: 100vh;
  display: grid;
  grid-template-columns: 240px 1fr;
}
.layout.no-sidebar {
  grid-template-columns: 1fr;
}

.content {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

/* ─── Sidebar ───────────────────────────────────────────────────── */
.sidebar {
  background: var(--sidebar-bg);
  color: var(--sidebar-text);
  border-right: 1px solid var(--sidebar-border);
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 0;
  height: 100vh;
  overflow-y: auto;
}
.brand-block {
  display: block;
  padding: 1.25rem 1.25rem 1rem;
  border-bottom: 1px solid var(--sidebar-border);
  text-decoration: none;
  color: inherit;
}
.brand-block:hover { opacity: 0.92; }
.brand-logo {
  display: block;
  width: 92px;
  height: auto;
  /* Force the SVG to render white on the burgundy sidebar — the
     source asset uses Web1 magenta, but the sidebar already
     carries the magenta cue, so the wordmark itself reads best
     in plain white. */
  filter: brightness(0) invert(1);
}
.brand-sub {
  margin-top: 0.45rem;
  font-size: 0.8rem;
  color: var(--sidebar-text-muted);
  letter-spacing: 0.02em;
}

.sidebar .nav {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding: 0.75rem 0.6rem;
  flex: 1;
}
.nav-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.55rem 0.8rem;
  border-radius: 8px;
  color: var(--sidebar-text);
  text-decoration: none;
  font-size: 0.92rem;
  line-height: 1.2;
}
.nav-item svg {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  opacity: 0.85;
}
.nav-item:hover {
  background: var(--sidebar-bg-2);
  color: #ffffff;
}
.nav-item.router-link-active {
  background: var(--sidebar-active-bg);
  color: var(--sidebar-active-text);
  font-weight: 600;
}
.nav-item.router-link-active svg { opacity: 1; }
.nav-section {
  margin: 0.85rem 0 0.3rem;
  padding: 0 0.85rem;
}
.nav-section-label {
  display: block;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--sidebar-text-muted);
}

/* ─── Topbar ────────────────────────────────────────────────────── */
.topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.65rem 1.5rem;
  background: var(--surface);
  border-bottom: 1px solid var(--border);
}
.topbar-left { display: flex; align-items: center; gap: 0.75rem; }
.topbar-right { display: flex; align-items: center; gap: 0.6rem; color: var(--text-muted); }

.user-dropdown { position: relative; }
.user-trigger {
  display: inline-flex; align-items: center; gap: 0.45rem;
  background: transparent; border: 1px solid transparent;
  padding: 0.3rem 0.6rem; border-radius: 6px; color: var(--text);
  cursor: pointer;
}
.user-trigger:hover { background: var(--surface-3); border-color: var(--border); }
.user-icon { width: 16px; height: 16px; opacity: 0.8; }
.user-trigger .caret { font-size: 0.75rem; color: var(--text-muted); }
.dropdown-menu {
  position: absolute; right: 0; top: calc(100% + 0.25rem);
  background: var(--surface); border: 1px solid var(--border);
  border-radius: 6px; box-shadow: 0 4px 14px rgb(0 0 0 / 0.12);
  display: flex; flex-direction: column;
  min-width: 200px; padding: 0.25rem 0; z-index: 50;
}
.menu-item {
  text-align: left;
  background: transparent; border: 0; border-radius: 0;
  padding: 0.55rem 0.95rem;
  color: var(--text); font-size: 0.9rem;
  cursor: pointer;
}
.menu-item:hover { background: var(--accent-bg); color: var(--accent-strong); }

.logout-direct {
  display: inline-flex; align-items: center; gap: 0.4rem;
  background: transparent;
  border: 1px solid var(--border);
  color: var(--text-muted);
  padding: 0.35rem 0.7rem;
  border-radius: 6px;
  font-size: 0.85rem;
}
.logout-direct svg { width: 16px; height: 16px; }
.logout-direct:hover {
  background: var(--surface-3);
  color: var(--text-strong);
}

button { cursor: pointer; padding: 0.35rem 0.85rem; border: 1px solid var(--border-strong); background: var(--surface); color: var(--text-strong); border-radius: 6px; }
button:hover { background: var(--surface-3); }

main {
  flex: 1;
  padding: 1.75rem 2rem;
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
}

/* Brand the .btn-primary across the GUI in both light and dark.
   Most components hardcode `#1e40af` (the old blue); promoting the
   selector at this top level overrides the scoped rules in both
   themes so the primary CTA carries the Web1 magenta. */
.btn-primary {
  background: var(--accent) !important;
  color: #ffffff !important;
  border-color: var(--accent) !important;
}
.btn-primary:hover:not(:disabled) {
  background: var(--accent-strong) !important;
  border-color: var(--accent-strong) !important;
}

/* ─────────────────────────────────────────────────────────────────
   Dark-mode overrides for component-scoped styles. Everything below
   matches the previous topbar-layout — the sidebar shell adds new
   surfaces but the component-level dark pass already exists.
   ───────────────────────────────────────────────────────────────── */

html[data-theme="dark"] label,
html[data-theme="dark"] dt {
  color: var(--text);
}
html[data-theme="dark"] .hint,
html[data-theme="dark"] .muted {
  color: var(--text-muted);
}

html[data-theme="dark"] .panel,
html[data-theme="dark"] .kpi,
html[data-theme="dark"] .card,
html[data-theme="dark"] .report-card,
html[data-theme="dark"] .state-group,
html[data-theme="dark"] .address-card,
html[data-theme="dark"] .filter-bar,
html[data-theme="dark"] .copy-from,
html[data-theme="dark"] .billing-profiles,
html[data-theme="dark"] .role-matrix-wrap,
html[data-theme="dark"] .login {
  background: var(--surface);
  border-color: var(--border);
  color: var(--text);
}

html[data-theme="dark"] .kpi.alert,
html[data-theme="dark"] .state.error {
  background: var(--error-bg);
  border-color: var(--error-border);
  color: var(--text);
}
html[data-theme="dark"] .state.error { color: var(--error); }

html[data-theme="dark"] .diff-row.kind-removed .before pre,
html[data-theme="dark"] .diff-row.kind-changed .before pre {
  background: var(--error-bg);
  color: var(--error);
}
html[data-theme="dark"] .diff-row.kind-added .after pre,
html[data-theme="dark"] .diff-row.kind-changed .after pre {
  background: var(--success-bg);
  color: var(--success);
}

html[data-theme="dark"] .state-pill[data-state="INACTIVE"],
html[data-theme="dark"] .status[data-status="INACTIVE"] {
  background: var(--error-bg);
  color: var(--error);
}

html[data-theme="dark"] .btn-secondary {
  background: var(--surface-2);
  color: var(--text);
  border-color: var(--border-strong);
}
html[data-theme="dark"] .btn-secondary:hover:not(:disabled),
html[data-theme="dark"] .btn-link:hover,
html[data-theme="dark"] .btn-clear:not(:disabled):hover,
html[data-theme="dark"] .preset-chip:hover {
  background: var(--surface-3);
}

html[data-theme="dark"] h1,
html[data-theme="dark"] h2,
html[data-theme="dark"] h3,
html[data-theme="dark"] h4 {
  color: var(--text-strong);
}
html[data-theme="dark"] .metric,
html[data-theme="dark"] .report-card header h3,
html[data-theme="dark"] .task-list a,
html[data-theme="dark"] .data-table a,
html[data-theme="dark"] .dialog-close:hover:not(:disabled) {
  color: var(--text-strong);
}
/* Vue scoped styles append a [data-v-xxxx] attribute which raises
   the rule's specificity to (0,3,0). The single-class override
   above sits at (0,2,1) and loses, so the .kpi .metric value
   ("0.00 €" / "23" / "0" / "2") still rendered black on the dark
   surface. The compound selector below matches the scoped rule's
   specificity (0,3,1) and wins.  */
html[data-theme="dark"] .kpi .metric,
html[data-theme="dark"] .kpi .num,
html[data-theme="dark"] .panel .metric {
  color: var(--text-strong);
}

html[data-theme="dark"] .section,
html[data-theme="dark"] .copy-label,
html[data-theme="dark"] .lines-block-head h3,
html[data-theme="dark"] .lines-toolbar h3,
html[data-theme="dark"] .contact-info,
html[data-theme="dark"] .when,
html[data-theme="dark"] .timeline .actor,
html[data-theme="dark"] .btn-clear,
html[data-theme="dark"] .preview-label,
html[data-theme="dark"] .preview-source {
  color: var(--text);
}

html[data-theme="dark"] .tab:hover {
  color: var(--text-strong);
}
html[data-theme="dark"] .tab.active,
html[data-theme="dark"] .role-cell.on {
  color: var(--accent-strong);
  border-bottom-color: var(--accent);
}

html[data-theme="dark"] .tag,
html[data-theme="dark"] .entity-link,
html[data-theme="dark"] .timeline .action,
html[data-theme="dark"] .role-chip[data-state="on"],
html[data-theme="dark"] .status,
html[data-theme="dark"] .state-pill[data-state="PROSPECT"] {
  background: var(--accent-bg);
  color: var(--accent-strong);
  border-color: var(--border);
}

html[data-theme="dark"] .data-table th,
html[data-theme="dark"] .audit-table th,
html[data-theme="dark"] .lines-table th,
html[data-theme="dark"] .role-matrix th,
html[data-theme="dark"] .invoice-lines th,
html[data-theme="dark"] .diff-table th {
  background: var(--surface-2);
  color: var(--text-muted);
  border-color: var(--border);
}
html[data-theme="dark"] .audit-table tr.row:hover,
html[data-theme="dark"] .data-table tbody tr:hover,
html[data-theme="dark"] .invoice-row:hover {
  background: var(--surface-3);
}

html[data-theme="dark"] .line-row,
html[data-theme="dark"] .copy-from,
html[data-theme="dark"] .dialog-foot,
html[data-theme="dark"] .invoice-detail-row > td {
  background: var(--surface-2);
  border-color: var(--border);
}

html[data-theme="dark"] .data-table,
html[data-theme="dark"] .lines-table,
html[data-theme="dark"] .role-matrix,
html[data-theme="dark"] .invoice-lines,
html[data-theme="dark"] .diff-table {
  background: var(--surface);
  color: var(--text);
}
html[data-theme="dark"] .data-table td,
html[data-theme="dark"] .lines-table td,
html[data-theme="dark"] .role-matrix td,
html[data-theme="dark"] .invoice-lines td,
html[data-theme="dark"] .diff-table td {
  border-color: var(--border);
}
html[data-theme="dark"] .invoice-row:hover,
html[data-theme="dark"] .data-table tbody tr:hover { background: var(--surface-2); }
html[data-theme="dark"] .invoice-detail-row > td { background: var(--surface-2); }

html[data-theme="dark"] input[type="text"],
html[data-theme="dark"] input[type="email"],
html[data-theme="dark"] input[type="password"],
html[data-theme="dark"] input[type="number"],
html[data-theme="dark"] input[type="date"],
html[data-theme="dark"] input[type="datetime-local"],
html[data-theme="dark"] input[type="search"],
html[data-theme="dark"] select,
html[data-theme="dark"] textarea {
  background: var(--surface-2);
  color: var(--text);
  border-color: var(--border-strong);
}
html[data-theme="dark"] input::placeholder,
html[data-theme="dark"] textarea::placeholder { color: var(--text-muted); }

html[data-theme="dark"] .dialog { background: var(--surface); color: var(--text); }
html[data-theme="dark"] .dialog-head,
html[data-theme="dark"] .dialog-foot {
  background: var(--surface-2);
  border-color: var(--border);
}
html[data-theme="dark"] .dialog-error { background: var(--error-bg); color: var(--error); border-color: var(--error-border); }

html[data-theme="dark"] .state-pill { background: var(--surface-3); color: var(--text); }
html[data-theme="dark"] .state-pill[data-state="ACTIVE"] { background: #14361f; color: #86efac; }
html[data-theme="dark"] .state-pill[data-state="DRAFT"] { background: #1e3a5f; color: #bfdbfe; }
html[data-theme="dark"] .state-pill[data-state="SIGNED"] { background: #3d2e0a; color: #fcd34d; }
html[data-theme="dark"] .state-pill[data-state="SUSPENDED"],
html[data-theme="dark"] .state-pill[data-state="DISPUTED"],
html[data-theme="dark"] .state-pill[data-state="PUSH_FAILED"] { background: #3f1d1d; color: #fca5a5; }
html[data-theme="dark"] .state-pill[data-state="PUSHED"] { background: #14361f; color: #86efac; }
html[data-theme="dark"] .state-pill[data-state="QUEUED"] { background: #3d2e0a; color: #fcd34d; }

html[data-theme="dark"] .tag,
html[data-theme="dark"] .preset-chip {
  background: var(--surface-3);
  color: var(--text);
  border-color: var(--border);
}
html[data-theme="dark"] .preset-chip.active { background: var(--accent); color: var(--surface); border-color: var(--accent); }
html[data-theme="dark"] .tag-add input { background: var(--surface-2); color: var(--text); }

html[data-theme="dark"] .login { box-shadow: 0 1px 3px rgb(0 0 0 / 0.4); }

/* Dashboard "Avaa raportti / Avaa lista / Avaa →" links and
   panel "see-all" links use literal #1e40af which collapses to
   dark-on-dark. Lift to the accent token so the links stay
   readable across themes. */
html[data-theme="dark"] .kpi-link,
html[data-theme="dark"] .see-all {
  color: var(--accent);
}

/* Dashboard task-list / data-table / timeline anchors hardcode
   #111827 (near-black) — invisible on dark surfaces. Promote to
   text-strong so the links are still distinguishable from body
   copy without glaring. */
html[data-theme="dark"] .task-list a,
html[data-theme="dark"] .data-table a {
  color: var(--text-strong);
}
html[data-theme="dark"] .task-list a:hover,
html[data-theme="dark"] .data-table a:hover {
  color: var(--accent);
}
html[data-theme="dark"] .timeline .actor {
  color: var(--text);
}

/* Dashboard "alert" KPI card (PUSH-VIRHEET red corner) hardcodes
   #fffaf9 / #fecaca which read white-ish on dark — we already
   overrode .kpi.alert background, but the dashboard's specific
   border-color override needs the same treatment. */
html[data-theme="dark"] .kpi.alert {
  background: var(--error-bg);
  border-color: var(--error-border);
}

/* Borders inside scoped panels (.task-list li, .data-table td,
   .timeline li) hardcode #f3f4f6 = bright on dark. Drop to the
   semantic border token. */
html[data-theme="dark"] .task-list li,
html[data-theme="dark"] .data-table td,
html[data-theme="dark"] .timeline li {
  border-color: var(--border);
}

/* ─────────────────────────────────────────────────────────────────
   Sweep #2: catch-all dark overrides for component-scoped styles
   that hardcode #374151 / #111827 / #6b7280 — they read black-on-
   black under the dark theme. Each selector below corresponds to
   a literal CSS rule somewhere in views/components; consolidating
   them here avoids touching every individual scoped style block.
   ───────────────────────────────────────────────────────────────── */
html[data-theme="dark"] .role-name,
html[data-theme="dark"] .resource-name {
  color: var(--text-strong);
}
html[data-theme="dark"] .action-name,
html[data-theme="dark"] .vars-desc,
html[data-theme="dark"] .field span,
html[data-theme="dark"] .field label,
html[data-theme="dark"] .check,
html[data-theme="dark"] .copy-label,
html[data-theme="dark"] form label > span,
html[data-theme="dark"] .lines-block-head h3,
html[data-theme="dark"] .lines-toolbar h3,
html[data-theme="dark"] .when {
  color: var(--text);
}

/* Secondary buttons across many components hardcode
   `background: white; color: #374151; border-color: #d1d5db`. The
   global override block earlier already handles `.btn-secondary`,
   but several components also style nested label spans + check
   rows the same way — covered above. */

/* ─────────────────────────────────────────────────────────────────
   Sweep #3: scoped tables / cards / hover rows that hardcoded
   white surfaces and #f9fafb hover backgrounds. The previous
   sweeps caught generic `.data-table`, `.panel`, `.card`, `.kpi`
   etc., but several views ship their own variants which slipped
   through and rendered glaring white in dark mode.
   ───────────────────────────────────────────────────────────────── */

/* ReportsView — brand-summary table at the bottom of the page. */
html[data-theme="dark"] .brand-summary-table {
  background: var(--surface);
  border-color: var(--border);
  color: var(--text);
}
html[data-theme="dark"] .brand-summary-table th {
  background: var(--surface-2);
  color: var(--text-muted);
}
html[data-theme="dark"] .brand-summary-table th,
html[data-theme="dark"] .brand-summary-table td {
  border-color: var(--border);
}

/* ProductFamiliesView — category group panels are #ffffff cards. */
html[data-theme="dark"] .cat-group {
  background: var(--surface);
  border-color: var(--border);
  color: var(--text);
}

/* EmailTemplatesView ships its own `.data-table tr.row:hover`
   rule with a bare `#f9fafb` that out-specificities the generic
   tbody hover override and turned the row glaring-white. Match
   the same selector in the dark override so it loses gracefully. */
html[data-theme="dark"] .data-table tr.row:hover {
  background: var(--surface-2);
}

/* ReportsView "row-link" anchors hardcode #1e40af — dark-on-dark.
   Lift to the magenta accent like the other link patterns. */
html[data-theme="dark"] .row-link {
  color: var(--accent);
}

/* ─────────────────────────────────────────────────────────────────
   Sweep #4: form controls inside scoped filter-bars + the
   SavedViewSelector. The generic `html[data-theme="dark"] select`
   override sits at (0,1,2); Vue-scoped rules like
   `.filter select[data-v-xxxx]` raise to (0,2,1) and win,
   keeping the controls white. The selectors below match a higher
   specificity so the dark surface tokens stick.
   ───────────────────────────────────────────────────────────────── */
html[data-theme="dark"] .filter-bar input,
html[data-theme="dark"] .filter-bar select,
html[data-theme="dark"] .filter input,
html[data-theme="dark"] .filter select,
html[data-theme="dark"] .filters input,
html[data-theme="dark"] .filters select,
html[data-theme="dark"] .saved-view-select {
  background: var(--surface-2);
  color: var(--text);
  border-color: var(--border-strong);
}
html[data-theme="dark"] .filter-label,
html[data-theme="dark"] .saved-view-label > span {
  color: var(--text-muted);
}

/* BrandsView edit dialog: the immutable Koodi field is rendered as
   a disabled `<input>` styled with `background: #f3f4f6` — bright
   grey on dark surface. Pull it down to the surface-3 token and
   keep the muted text. The same scoped rule pattern shows up in
   other "locked" inputs across the GUI; match the broad selector
   so future occurrences don't need a fresh override. */
html[data-theme="dark"] input:disabled,
html[data-theme="dark"] select:disabled,
html[data-theme="dark"] textarea:disabled {
  background: var(--surface-3);
  color: var(--text-muted);
  border-color: var(--border);
}

/* ContactFormDialog role chips: the default (off) state hardcodes
   `background: white`, the "on" state hardcodes #eef2ff (pale
   indigo), and "primary" hardcodes #1e40af. Lift them all to the
   dark surface tokens / brand magenta so the role grid stays
   legible and on-brand. */
html[data-theme="dark"] .role-chip {
  background: var(--surface-2);
  color: var(--text);
  border-color: var(--border-strong);
}
html[data-theme="dark"] .role-chip[data-state="on"] {
  background: var(--accent-bg);
  color: var(--accent-strong);
  border-color: var(--border-strong);
}
html[data-theme="dark"] .role-chip[data-state="primary"] {
  background: var(--accent);
  color: #ffffff;
  border-color: var(--accent);
}
html[data-theme="dark"] .btn-clear {
  background: var(--surface-2);
  color: var(--text);
  border-color: var(--border-strong);
}

/* The sidebar is always dark — make sure scoped styles inside it
   don't get hit by global dark overrides. */
html[data-theme="dark"] .sidebar {
  background: var(--sidebar-bg);
  color: var(--sidebar-text);
}
</style>
